// Само поле ввода и тулбар под ним — карточка набора запроса. Всё, что выше
// (сводка настроек, колонки входов, предупреждения), живёт в баре: здесь только
// то, чем человек набирает и отправляет.

import { useTranslation } from "react-i18next";

import { PROMPT } from "~/shared/lib/design-tokens";

import { PromptToolbar } from "./PromptToolbar";
import { MAX_LENGTH } from "./usePromptDraft";

import type { useChatAttachments } from "./useChatAttachments";
import type { usePromptDraft } from "./usePromptDraft";

export interface PromptComposerProps {
    chatId: string | null;
    disabled: boolean;
    draft: ReturnType<typeof usePromptDraft>;
    attachments: ReturnType<typeof useChatAttachments>;
    /** Есть ли что очищать — и текст, и вложения, и элементы. */
    canClear: boolean;
    onClear: () => void;
    onToggleSettings: () => void;
    costLabel: string;
    canSend: boolean;
    /** Почему отправка недоступна; null — кнопка рабочая. */
    blockedHint: string | null;
    onSend: () => void;
}

export function PromptComposer({
    chatId,
    disabled,
    draft,
    attachments,
    canClear,
    onClear,
    onToggleSettings,
    costLabel,
    canSend,
    blockedHint,
    onSend,
}: PromptComposerProps) {
    const { t } = useTranslation("chat");

    return (
        <div className="rounded-[1rem] bg-zinc-900/80 ring-1 ring-white/10 backdrop-blur-xl transition-[box-shadow] duration-150 focus-within:ring-white/20">
            <div className={PROMPT.innerPad}>
                <textarea
                    ref={draft.textareaRef}
                    value={draft.text}
                    onChange={(event) => draft.onTextChange(event.target.value)}
                    onKeyDown={draft.handleKeyDown}
                    onPaste={attachments.handlePaste}
                    placeholder={t("prompt.placeholder")}
                    maxLength={MAX_LENGTH}
                    rows={1}
                    disabled={disabled}
                    className="scrollbar-hide w-full resize-none bg-transparent text-base leading-relaxed text-zinc-100 outline-none placeholder:text-zinc-500 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ maxHeight: 200 }}
                />
            </div>

            <PromptToolbar
                chatId={chatId}
                disabled={disabled}
                canClear={canClear}
                onClear={onClear}
                onToggleSettings={onToggleSettings}
                supportsAnyAttachment={attachments.supportsAnyAttachment}
                allSlotsFull={attachments.allSlotsFull}
                maxImages={attachments.maxImages}
                currentImagesCount={attachments.totalImagesInQueue}
                onPickFiles={attachments.handlePickFiles}
                accept={attachments.fileInputAccept}
                // Скрепка тулбара кладёт в пул, а не в роль-слот и не в референсы.
                onOpenUploadedPicker={() => attachments.openPicker(undefined, attachments.setPickerOpen)}
                onOpenGeneratedPicker={() =>
                    attachments.openPicker(undefined, attachments.setGeneratedPickerOpen)
                }
                costLabel={costLabel}
                canSend={canSend}
                blockedHint={blockedHint}
                onSend={onSend}
            />
        </div>
    );
}
